<template>
  <div
    class="w-full h-auto min-h-screen justify-between space-y-9 rounded-t-[20px]"
  >
    <div class="mx-">
      <Loader v-if="loading" />
      <div v-else class="space-y-4">
        <div class="hr-container space-x-5">
          <div class="flex items-center justify-between">
            <div class="space-y-2">
              <p class="font-semibold text-foreground text-[20px]">
                {{ showResults ? "Assessment Results" : "AI Assessment" }}
              </p>
              <p class="text-muted-foreground text-[14px]">
                {{
                  showResults
                    ? "View your assessment results"
                    : currentAssessmentType === "mcq"
                    ? "Part 1 - Multiple Choice Questions"
                    : "Part 2 - Oral Assessment"
                }}
              </p>
            </div>

            <!-- <div class="flex gap-2" v-if="goal">
              <button
                @click="goBack"
                class="flex bg-black text-white text-[14px] font-medium px-6 py-3 space-x-4 items-center rounded-full h-[42px] border border-black dark:bg-muted/70"
              >
                <span>Review Course</span>
              </button>
            </div> -->
          </div>
        </div>

        <!-- Results View -->
        <div v-if="showResults" class="mt-4">
          <div class="rounded-[12px] border border-border bg-card">
            <div class="px-6 py-5">
              <div class="mb-5">
                <p class="text-[16px] font-semibold text-[#4C4D4F]">
                  All assessments complete!
                </p>
                <p class="text-[14px] text-[#667085] mt-1">
                  Great job! Your assessor has submitted their evaluation.
                  You’ll receive your combined AI + Human assessment report
                  within 24 hours.
                  <br />Here's a summary of your assessment results:
                </p>
              </div>

              <div class="grid grid-cols-1 gap-6">
                <div class="space-y-1">
                  <div class="grid grid-cols-3 gap-4">
                    <div>
                      <p class="text-[16px] font-semibold text-[#4C4D4F]">
                        Stage
                      </p>
                      <p class="text-[13px] text-[#667085]">AI Assessment</p>
                      <p class="text-[13px] text-[#667085]">Human Assessment</p>
                      <p class="text-[13px] text-[#667085]">Certification</p>
                    </div>
                    <div>
                      <p class="text-[16px] font-semibold text-[#4C4D4F]">
                        Status
                      </p>
                      <p class="text-[13px] text-[#667085]">{{ aiStatus }}</p>
                      <p class="text-[13px] text-[#667085]">
                        {{ humanStatus }}
                      </p>
                      <p class="text-[13px] text-[#667085]">
                        {{ certificationStatus }}
                      </p>
                    </div>
                    <div>
                      <p class="text-[16px] font-semibold text-[#4C4D4F]">
                        Points
                      </p>
                      <p class="text-[13px] text-[#667085]">
                        {{ aiScore }} points
                      </p>
                      <p class="text-[13px] text-[#667085]">
                        {{ humanScore }} points
                      </p>
                      <p class="text-[13px] text-[#667085]"></p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-6">
                <button
                  @click="goToCertificates"
                  class="px-4 py-2 text-[13px] font-medium bg-black text-white rounded-[8px] hover:opacity-90"
                >
                  View certificates
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div
          v-if="!showResults && error"
          class="mt-6 p-4 bg-red-50 border border-red-300 rounded-[8px]"
        >
          <p class="text-[14px] text-red-700">{{ error }}</p>
          <div class="flex gap-3 mt-3">
            <button
              @click="retryInitialization"
              class="px-4 py-2 text-[13px] font-medium text-white bg-red-600 border border-red-600 rounded-[6px] hover:bg-red-700"
            >
              Retry
            </button>
            <button
              @click="goBack"
              class="px-4 py-2 text-[13px] font-medium text-red-700 bg-white border border-red-300 rounded-[6px] hover:bg-red-50"
            >
              Go Back
            </button>
          </div>
        </div>

        <!-- Intro Screen -->
        <div v-if="showIntro && !showResults && !error" class="mt-6">
          <div
            class="rounded-[12px] px-4 md:px-6 py-4 md:py-6 bg-card border border-border"
          >
            <div class="mb-4">
              <h3
                class="text-[16px] md:text-[18px] font-semibold text-foreground mb-2"
              >
                {{
                  currentAssessmentType === "mcq"
                    ? "Part 1: Ready for your first assessment?"
                    : "Part 2: Oral Problem-Solving Assessment"
                }}
              </h3>
              <p class="text-[13px] md:text-[14px] text-muted-foreground">
                <template v-if="currentAssessmentType === 'mcq'">
                  This section tests your understanding of the concepts you've
                  learned in
                  <span class="font-semibold">{{
                    goal?.topic || "this subject"
                  }}</span
                  >. You'll answer a short set of multiple-choice questions
                  generated by our AI system.
                </template>
                <template v-else>
                  Demonstrate your practical problem-solving skills in
                  <span class="font-semibold">{{
                    goal?.topic || "this subject"
                  }}</span
                  >.
                </template>
              </p>
            </div>

            <!-- MCQ Details -->
            <div v-if="currentAssessmentType === 'mcq'" class="space-y-6 my-6">
              <div
                class="bg-gray-50 dark:bg-muted/30 rounded-[12px] p-2 md:p-5"
              >
                <div
                  class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-3"
                >
                  <div>
                    <p class="text-[12px] font-semibold text-foreground">
                      Estimated Time
                    </p>
                    <p class="text-[14px] text-muted-foreground">
                      {{ mcqData.timeLimit || 15 }} minutes
                    </p>
                  </div>

                  <div>
                    <p class="text-[12px] font-semibold text-foreground">
                      Number of Questions
                    </p>
                    <p class="text-[14px] text-muted-foreground">
                      {{ mcqData.questions.length || 0 }}
                    </p>
                  </div>
                  <div>
                    <p class="text-[12px] font-semibold text-foreground">
                      Format
                    </p>
                    <p class="text-[14px] text-muted-foreground">
                      {{
                        mcqData.format || "Multiple choice (one correct answer)"
                      }}
                    </p>
                  </div>

                  <div>
                    <p class="text-[12px] font-semibold text-foreground">
                      Passing Score
                    </p>
                    <p class="text-[14px] text-muted-foreground">
                      {{ mcqData.passingScore || 70 }}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Oral Assessment Details -->
            <div
              v-else-if="currentAssessmentType === 'oral'"
              class="space-y-6 my-6"
            >
              <div
                class="bg-gray-50 dark:bg-muted/30 rounded-[12px] p-4 md:p-5"
              >
                <div
                  class="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-3"
                >
                  <div>
                    <p class="text-[12px] font-semibold text-foreground">
                      Number of Scenarios
                    </p>
                    <p class="text-[14px] text-bodyText">
                      {{ oralData.questions.length || 0 }}
                    </p>
                  </div>
                  <div>
                    <p class="text-[12px] font-semibold text-foreground">
                      Estimated Time
                    </p>
                    <p class="text-[14px] text-bodyText">
                      {{ oralData.estimatedTime || 15 }} minutes
                    </p>
                  </div>
                  <div>
                    <p class="text-[12px] font-semibold text-muted-foreground">
                      Format
                    </p>
                    <p class="text-[14px] text-bodyText">Voice Recording</p>
                  </div>
                </div>
                <p class="text-[13px] text-gray-600 mt-3">
                  You'll record your voice as you explain your thought process
                  and solution to scenario-based questions.
                </p>
              </div>
            </div>

            <div
              class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-[8px] p-2 md:p-4 mb-6"
            >
              <div class="flex items-start gap-2">
                <span class="text-blue-600 dark:text-blue-400 text-[16px]"
                  >💡</span
                >
                <div>
                  <p
                    class="text-[13px] font-medium text-blue-800 dark:text-blue-300 mb-1"
                  >
                    Important Tips:
                  </p>
                  <ul
                    class="text-[13px] text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside"
                  >
                    <li>Ensure you have a stable internet connection</li>
                    <li v-if="currentAssessmentType === 'oral'">
                      Find a quiet environment for recording
                    </li>
                    <li v-if="currentAssessmentType === 'oral'">
                      Allow microphone access when prompted
                    </li>
                    <li>
                      The assessment will auto-submit if you navigate away
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-3">
              <button
                @click="proceedFromIntro"
                class="w-full sm:w-auto px-6 py-3 text-[14px] font-semibold text-white bg-black rounded-full hover:opacity-90 transition-opacity"
              >
                {{
                  currentAssessmentType === "mcq"
                    ? "Start AI Assessment"
                    : "Begin Oral Assessment"
                }}
              </button>
              <!-- <button
                @click="goBack"
                class="px-6 py-3 text-[14px] font-medium text-bodyText border border-border dark:border-borderDark rounded-[8px] hover:bg-gray-50 dark:hover:bg-muted/50 transition-colors"
              >
                Cancel
              </button> -->
            </div>
          </div>
        </div>

        <div
          class="border border-border dark:border-borderDark px-2 md:px-5 py-2 md:py-6 rounded-[20px] bg-white dark:bg-background"
          v-if="!showIntro"
        >
          <!-- Assessment Progress -->
          <div v-if="!showResults && !error" class="mt-6 mb-8">
            <div class="flex items-center gap-4">
              <span class="text-[14px] font-semibold text-bodyText dark:text-muted-foreground shrink-0">
                {{
                  currentAssessmentType === "mcq"
                    ? `${answeredCount}/${totalQuestions}`
                    : `${currentOralQuestionIndex + 1}/${totalQuestions}`
                }}
              </span>
              <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden dark:bg-muted-foreground">
                <div
                  class="h-full bg-black transition-all duration-300 dark:bg-muted/80"
                  :style="{ width: progressBarPercent + '%' }"
                ></div>
              </div>
              <span class="text-[14px] font-semibold text-bodyText dark:text-muted-foreground shrink-0">{{
                countdownDisplay
              }}</span>
            </div>
          </div>

          <!-- MCQ Assessment View: all questions stacked in parent card -->
          <div
            v-if="!showResults && currentAssessmentType === 'mcq' && !error"
            class="mt-8"
          >
            <div
              v-if="mcqData.questions.length > 0"
              class="rounded-[12px] px-2 md:px-6 py-4 md:py-6 bg-primaryBg dark:bg-muted/30"
            >
              <div class="space-y-6">
                <div
                  v-for="(q, qIndex) in mcqData.questions"
                  :key="q.question_id || qIndex"
                  class="border border-border dark:border-borderDark rounded-[12px] p-2 md:p-6 bg-card dark:bg-card"
                >
                  <div class="mb-4">
                    <h2
                      class="text-[12px] font-light italic text-muted-foreground"
                    >
                      Question {{ qIndex + 1 }}
                    </h2>
                    <p
                      class="text-[14px] md:text-[15px] text-foreground dark:text-foreground font-medium"
                    >
                      {{ q.question }}
                    </p>
                  </div>

                  <div class="space-y-3">
                    <label
                      v-for="(option, optIndex) in q.options"
                      :key="option.option_id || optIndex"
                      class="flex items-start p-3 md:p-4 border border-gray-300 dark:border-borderDark rounded-[8px] cursor-pointer hover:bg-gray-50 dark:hover:bg-muted/50 transition-colors"
                      :class="{
                        'bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-400':
                          selectedAnswers[qIndex] ===
                          (option.option_id || optIndex),
                      }"
                    >
                      <input
                        type="radio"
                        :value="option.option_id || optIndex"
                        v-model="selectedAnswers[qIndex]"
                        class="mt-1 cursor-pointer"
                      />
                      <span
                        class="ml-3 text-[13px] md:text-[14px] text-foreground dark:text-foreground"
                        >{{ option.text }}</span
                      >
                    </label>
                  </div>
                </div>
              </div>

              <div class="flex justify-end mt-2 mb-15">
                <button
                  @click="completeMCQAndStartOral"
                  :disabled="answeredCount < totalQuestions || submittingMCQ"
                  class="w-full sm:w-auto px-6 py-3 text-[14px] font-medium text-white dark:text-foreground bg-black rounded-full hover:opacity-90 dark:hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span
                    v-if="submittingMCQ"
                    class="inline-flex items-center gap-2"
                  >
                    <svg
                      class="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting…
                  </span>
                  <span v-else>Submit</span>
                </button>
              </div>
            </div>
            <div v-else class="text-center py-8">
              <p class="text-gray-500 text-[14px]">No questions available.</p>
            </div>
          </div>

          <!-- Oral Assessment View -->
          <div
            v-else-if="
              !showResults && currentAssessmentType === 'oral' && !error
            "
            class="mt-8"
          >
            <div
              class="rounded-[12px] px-6 py-6 bg-card dark:bg-card border border-border dark:border-borderDark"
            >
              <!-- Intro info should only show before starting the oral assessment -->
              <div v-if="!oralAssessmentStarted">
                <div class="mb-4">
                  <h3 class="text-[16px] font-semibold text-foreground mb-2">
                    Oral Problem-Solving Assessment
                  </h3>
                  <p
                    class="text-[14px] text-gray-600 dark:text-muted-foreground"
                  >
                    This section is designed to assess your ability to think
                    critically, communicate clearly, and apply your knowledge to
                    real-world scenarios.
                  </p>
                </div>

                <div
                  class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 my-6"
                >
                  <div>
                    <p class="text-[12px] font-semibold text-muted-foreground">
                      Estimated Time
                    </p>
                    <p class="text-[14px] text-foreground dark:text-foreground">
                      {{ oralData.estimatedTime || 15 }} minutes
                    </p>
                  </div>
                  <div>
                    <p class="text-[12px] font-semibold text-muted-foreground">
                      Number of Questions
                    </p>
                    <p class="text-[14px] text-foreground dark:text-foreground">
                      {{ oralData.questions.length || 0 }}
                    </p>
                  </div>
                  <div>
                    <p class="text-[12px] font-semibold text-muted-foreground">
                      Format
                    </p>
                    <p class="text-[14px] text-foreground dark:text-foreground">
                      Scenario-based
                    </p>
                  </div>
                </div>

                <div class="mb-4">
                  <p
                    class="text-[14px] text-gray-600 dark:text-muted-foreground"
                  >
                    You'll record your voice as you explain your thought process
                    and solution.
                  </p>
                  <p
                    class="text-[13px] text-gray-700 dark:text-muted-foreground mt-2 font-medium"
                  >
                    <span class="mr-2">💡</span>Note: Your recordings will be
                    saved automatically and reviewed.
                  </p>
                </div>
              </div>

              <div v-if="!oralAssessmentStarted" class="mt-6">
                <button
                  @click="startOralAssessment"
                  :disabled="startingOral"
                  class="px-6 py-3 text-[14px] font-medium text-white bg-black dark:bg-primaryDark rounded-[8px] hover:opacity-90 dark:hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span
                    v-if="startingOral"
                    class="inline-flex items-center gap-2"
                  >
                    <svg
                      class="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Starting…
                  </span>
                  <span v-else>Start Oral Assessment</span>
                </button>
              </div>

              <div v-else-if="currentOralQuestion" class="mt-6">
                <!-- Question Header -->
                <div class="mb-6">
                  <h3 class="text-[18px] font-semibold text-bodyText mb-4">
                    Scenario {{ currentOralQuestionIndex + 1 }}
                  </h3>
                  <p class="text-[14px] text-gray-700 leading-relaxed">
                    {{ currentOralQuestion.question }}
                  </p>
                </div>

                <!-- Recording Interface -->
                <div class="flex flex-col items-center justify-center py-12">
                  <!-- Microphone Button -->
                  <button
                    @click="
                      recordingInterval ? stopRecording() : startRecording()
                    "
                    :class="[
                      'w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300',
                      recordingInterval
                        ? 'bg-red-600 animate-pulse shadow-lg shadow-red-300'
                        : 'bg-[#292D32] hover:bg-[#3A3E47]',
                    ]"
                  >
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 64 64"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M32.0002 37.3335C29.7779 37.3335 27.8891 36.5557 26.3335 35.0002C24.7779 33.4446 24.0002 31.5557 24.0002 29.3335V13.3335C24.0002 11.1113 24.7779 9.22238 26.3335 7.66683C27.8891 6.11127 29.7779 5.3335 32.0002 5.3335C34.2224 5.3335 36.1113 6.11127 37.6668 7.66683C39.2224 9.22238 40.0002 11.1113 40.0002 13.3335V29.3335C40.0002 31.5557 39.2224 33.4446 37.6668 35.0002C36.1113 36.5557 34.2224 37.3335 32.0002 37.3335ZM29.3335 56.0002V47.8002C24.7113 47.1779 20.8891 45.1113 17.8668 41.6002C14.8446 38.0891 13.3335 34.0002 13.3335 29.3335H18.6668C18.6668 33.0224 19.9668 36.1668 22.5668 38.7668C25.1668 41.3668 28.3113 42.6668 32.0002 42.6668C35.6891 42.6668 38.8335 41.3668 41.4335 38.7668C44.0335 36.1668 45.3335 33.0224 45.3335 29.3335H50.6668C50.6668 34.0002 49.1557 38.0891 46.1335 41.6002C43.1113 45.1113 39.2891 47.1779 34.6668 47.8002V56.0002H29.3335ZM32.0002 32.0002C32.7557 32.0002 33.3891 31.7446 33.9002 31.2335C34.4113 30.7224 34.6668 30.0891 34.6668 29.3335V13.3335C34.6668 12.5779 34.4113 11.9446 33.9002 11.4335C33.3891 10.9224 32.7557 10.6668 32.0002 10.6668C31.2446 10.6668 30.6113 10.9224 30.1002 11.4335C29.5891 11.9446 29.3335 12.5779 29.3335 13.3335V29.3335C29.3335 30.0891 29.5891 30.7224 30.1002 31.2335C30.6113 31.7446 31.2446 32.0002 32.0002 32.0002Z"
                        fill="white"
                      />
                    </svg>
                  </button>

                  <!-- Recording Timer:-->
                  <div
                    v-if="recordingInterval"
                    class="mt-6 text-[24px] font-semibold text-bodyText"
                  >
                    {{ recordingTime }}
                  </div>

                  <!-- Audio Preview: show after recording stops and blob is available -->
                  <div v-if="recordedBlobUrl" class="mt-8 w-full max-w-2xl">
                    <!-- Waveform Visualization -->
                    <div class="p-6">
                      <div ref="waveformContainer" class="mb-4"></div>

                      <div class="flex items-center justify-center gap-4">
                        <button
                          @click="togglePlayback"
                          class="flex items-center justify-center hover:opacity-70 transition-opacity"
                        >
                          <svg
                            v-if="!isPlaying"
                            width="12"
                            height="14"
                            viewBox="0 0 12 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M0 0V14L12 7L0 0Z" fill="#000000" />
                          </svg>
                          <svg
                            v-else
                            width="12"
                            height="14"
                            viewBox="0 0 12 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect width="4" height="14" fill="#000000" />
                            <rect x="8" width="4" height="14" fill="#000000" />
                          </svg>
                        </button>
                        <span class="text-[16px] font-medium text-bodyText">{{
                          playbackDisplay
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Action Buttons: always visible; disabled until there's recorded time -->
                  <div class="flex gap-4 mt-8">
                    <button
                      @click="submitOralAnswer"
                      :disabled="
                        recordingSeconds <= 0 ||
                        submittingOralAnswer ||
                        recordingInterval
                      "
                      class="px-6 py-3 text-[14px] font-medium text-white bg-black rounded-[8px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span class="inline-flex items-center gap-2">
                        <template v-if="submittingOralAnswer">
                          <svg
                            class="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Submitting…
                        </template>
                        <template v-else>
                          Submit Response
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2 13.3337V9.33366L7.33333 8.00033L2 6.66699V2.66699L14.6667 8.00033L2 13.3337Z"
                              fill="#FFFFFF"
                            />
                          </svg>
                        </template>
                      </span>
                    </button>
                    <button
                      @click="retryRecording"
                      :disabled="
                        recordingSeconds <= 0 ||
                        submittingOralAnswer ||
                        recordingInterval
                      "
                      class="px-6 py-3 text-[14px] font-medium text-white bg-black rounded-[8px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span class="inline-flex items-center gap-2">
                        Retake Response
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 11 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5.33333 10.6667C3.84444 10.6667 2.58333 10.15 1.55 9.11667C0.516667 8.08333 0 6.82222 0 5.33333C0 3.84444 0.516667 2.58333 1.55 1.55C2.58333 0.516667 3.84444 0 5.33333 0C6.1 0 6.83333 0.158333 7.53333 0.475C8.23333 0.791667 8.83333 1.24444 9.33333 1.83333V0H10.6667V4.66667H6V3.33333H8.8C8.44444 2.71111 7.95833 2.22222 7.34167 1.86667C6.725 1.51111 6.05556 1.33333 5.33333 1.33333C4.22222 1.33333 3.27778 1.72222 2.5 2.5C1.72222 3.27778 1.33333 4.22222 1.33333 5.33333C1.33333 6.44444 1.72222 7.38889 2.5 8.16667C3.27778 8.94444 4.22222 9.33333 5.33333 9.33333C6.18889 9.33333 6.96111 9.08889 7.65 8.6C8.33889 8.11111 8.82222 7.46667 9.1 6.66667H10.5C10.1889 7.84444 9.55556 8.80556 8.6 9.55C7.64444 10.2944 6.55556 10.6667 5.33333 10.6667Z"
                            fill="white"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Human Assessment -->
          <div
            v-if="!showResults && !error && showHumanAssessment"
            class="mt-8"
          >
            <div
              class="max-w-3xl mx-auto bg-card rounded-[12px] border border-border p-8"
            >
              <h2 class="text-[20px] font-semibold text-bodyText mb-2">
                Human Assessment
              </h2>
              <p class="text-[14px] text-gray-600 mb-6">Interview stage</p>

              <div class="bg-gray-50 rounded-[8px] p-6 mb-6">
                <p class="text-[14px] text-gray-700 leading-relaxed">
                  Welcome to your human assessment
                </p>
                <p class="text-[14px] text-gray-700 leading-relaxed mt-3">
                  You've successfully completed your AI Assessment. The next
                  step is a short interview with one of our certified assessors
                  who will review your performance and discuss your results.
                </p>
              </div>

              <div class="grid grid-cols-3 gap-6 mb-6">
                <div>
                  <p class="text-[12px] text-gray-500 mb-1">
                    {{
                      assessmentBooking ? "Scheduled Time" : "Estimated Time"
                    }}
                  </p>
                  <p class="text-[14px] font-medium text-bodyText">
                    {{
                      assessmentBooking
                        ? `${assessmentBooking.start_time} - ${assessmentBooking.end_time}`
                        : "15-30 minutes"
                    }}
                  </p>
                </div>
                <div>
                  <p class="text-[12px] text-gray-500 mb-1">Medium</p>
                  <p class="text-[14px] font-medium text-bodyText">
                    Google Meet
                  </p>
                </div>
                <div>
                  <p class="text-[12px] text-gray-500 mb-1">Format</p>
                  <p class="text-[14px] font-medium text-bodyText">
                    1:1 live interview
                  </p>
                </div>
              </div>

              <div
                class="bg-yellow-50 border border-yellow-200 rounded-[8px] p-4 mb-6"
              >
                <div class="flex items-start gap-2">
                  <span class="text-yellow-600 text-[16px]">💡</span>
                  <div>
                    <p class="text-[13px] font-medium text-yellow-800 mb-1">
                      Tip:
                    </p>
                    <p class="text-[13px] text-yellow-700">
                      Prepare to discuss your AI assessment responses and
                      demonstrate your understanding of the concepts covered.
                    </p>
                  </div>
                </div>
              </div>

              <div class="flex gap-3">
                <button
                  v-if="!assessmentBooking"
                  @click="scheduleHumanAssessment"
                  class="flex-1 bg-primaryDarkBg text-white py-3 px-6 rounded-[8px] text-[14px] font-medium hover:bg-opacity-90 transition-all"
                >
                  Schedule Human Assessment
                </button>
                <button
                  @click="viewAssessmentResults"
                  class="flex-1 bg-white border border-gray-300 text-bodyText py-3 px-6 rounded-[8px] text-[14px] font-medium hover:bg-gray-50 transition-all"
                >
                  View Assessment Results
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Exit Confirmation Modal -->
    <div
      v-if="showExitModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        class="bg-card rounded-[12px] shadow-xl w-[90%] max-w-md p-6 border border-border"
      >
        <h3 class="text-[16px] font-semibold text-foreground mb-2">
          Leave Assessment?
        </h3>
        <p class="text-[14px] text-muted-foreground mb-4">
          If you leave, the assessment will be automatically submitted. This
          includes closing the tab, opening a new window, minimizing the
          browser, or navigating away.
        </p>
        <div class="flex justify-end gap-3">
          <button
            @click="cancelExit"
            class="px-4 py-2 text-[14px] font-medium bg-white text-gray-800 border border-gray-300 rounded-[8px] hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="confirmExit"
            class="px-4 py-2 text-[14px] font-medium bg-black text-white rounded-[8px] hover:opacity-90"
          >
            Confirm {{ "&" }} Submit
          </button>
        </div>
      </div>
    </div>

    <BookSessionModal
      :show="showBookingModal"
      :mentorName="'Mentor'"
      :projectId="projectId"
      @close="closeBookingModal"
      @request-session="handleBookMentor"
    />
  </div>
</template>

<script>
import apiClient from "@/axios";
import BookSessionModal from "./BookSessionModal.vue";
import WaveSurfer from "wavesurfer.js";
import Loader from "../ui/Loader.vue";

export default {
  name: "AIAssessmentComponent",
  components: {
    BookSessionModal,
    Loader,
  },
  data() {
    return {
      goal: null,
      loading: true,
      showResults: false,
      showIntro: false,
      results: null,
      showHumanAssessment: false,
      showBookingModal: false,
      currentAssessmentType: "mcq", // 'mcq' or 'oral'
      mcqData: {
        questions: [],
        timeLimit: 15,
        passingScore: 70,
        alreadyCompleted: false,
        previousScore: null,
      },
      oralData: {
        questions: [],
        estimatedTime: 15,
        evaluationCriteria: {},
        alreadyCompleted: false,
        alreadyReviewed: false,
        previousScore: null,
      },
      currentQuestionIndex: 0,
      selectedAnswers: {},
      oralAssessmentStarted: false,
      currentOralQuestionIndex: 0,
      recordingTime: "00:00",
      recordingInterval: null,
      recordingSeconds: 0,
      mediaRecorder: null,
      mediaStream: null,
      audioChunks: [],
      recordedBlob: null,
      recordedMimeType: null,
      recordedBlobUrl: null,
      uploadingOral: false,
      countdownSeconds: 0,
      countdownInterval: null,
      error: null,
      submittingMCQ: false,
      startingOral: false,
      submittingOralAnswer: false,
      showExitModal: false,
      pendingExitResolver: null,
      audioElement: null,
      isPlaying: false,
      currentPlaybackTime: 0,
      audioDuration: 0,
      wavesurfer: null,
      assessmentBooking: null,
    };
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
    currentQuestion() {
      if (this.currentAssessmentType === "mcq") {
        return this.mcqData.questions[this.currentQuestionIndex];
      } else {
        return this.oralData.questions[this.currentOralQuestionIndex];
      }
    },
    totalQuestions() {
      if (this.currentAssessmentType === "mcq") {
        return this.mcqData.questions.length;
      } else {
        return this.oralData.questions.length;
      }
    },
    currentOralQuestion() {
      return this.oralData.questions[this.currentOralQuestionIndex] || null;
    },
    answeredCount() {
      // Count how many MCQ questions have an answer selected
      if (this.currentAssessmentType !== "mcq") return 0;
      return Object.keys(this.selectedAnswers).filter(
        (k) =>
          this.selectedAnswers[k] !== null &&
          this.selectedAnswers[k] !== undefined,
      ).length;
    },
    progressBarPercent() {
      if (this.currentAssessmentType === "mcq") {
        return this.totalQuestions > 0
          ? Math.round((this.answeredCount / this.totalQuestions) * 100)
          : 0;
      }
      if (this.oralAssessmentStarted && this.totalQuestions > 0) {
        return Math.round(
          ((this.currentOralQuestionIndex + 1) / this.totalQuestions) * 100,
        );
      }
      return 0;
    },
    countdownDisplay() {
      const m = Math.floor(this.countdownSeconds / 60);
      const s = this.countdownSeconds % 60;
      return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    },
    playbackDisplay() {
      const m = Math.floor(this.currentPlaybackTime / 60);
      const s = Math.floor(this.currentPlaybackTime % 60);
      return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    },
    playbackProgress() {
      return this.audioDuration > 0
        ? (this.currentPlaybackTime / this.audioDuration) * 100
        : 0;
    },
    aiScore() {
      const mcq = this.results?.mcq_result?.score ?? 0;
      const oral = this.results?.oral_result?.score ?? 0;
      const count = (mcq > 0 ? 1 : 0) + (oral > 0 ? 1 : 0);
      if (count === 0) return "-";
      const avg = (mcq + oral) / count;
      return Math.round(avg);
    },
    aiStatus() {
      const hasMcq = !!this.results?.mcq_result;
      const hasOral = !!this.results?.oral_result;
      if (hasMcq && hasOral) return "Completed ✅";
      if (hasMcq || hasOral) return "In Progress";
      return "Pending";
    },
    humanStatus() {
      const hr = this.results?.human_result;
      if (!hr) return "Disabled";
      if (hr.reviewed_at) return "Completed ✅";
      return hr.enabled ? "In Review" : "Disabled";
    },
    humanScore() {
      const s = this.results?.human_result?.score;
      return s ?? "-";
    },
    certificationStatus() {
      return this.results?.completed_at ? "Ready" : "In Review";
    },
    certificationScore() {
      return this.results?.overall_score ?? "-";
    },
    shouldShowHumanAssessment() {
      return (
        this.goal?.has_mentor === true &&
        this.goal?.human_assessment_progress?.progress !== 100
      );
    },
    isHumanAssessmentCompleted() {
      return this.goal?.human_assessment_progress?.progress === 100;
    },
  },
  methods: {
    async startAssessment(part) {
      try {
        await apiClient.post(
          `/web/learning-goals/${this.projectId}/assessment/start`,
          { part },
        );
      } catch (err) {
        console.error("Error starting assessment:", err);
      }
    },
    async fetchAssessmentResults() {
      try {
        this.loading = true;
        const res = await apiClient.get(
          `/web/learning-goals/${this.projectId}/assessment/results`,
        );
        this.results = res.data;
        this.showResults = true;
        this.error = null;
      } catch (err) {
        console.error("Error fetching assessment results:", err);
        this.error =
          err?.response?.data?.detail || "Failed to load assessment results";
      } finally {
        this.loading = false;
      }
    },
    async fetchGoal() {
      try {
        this.loading = true;
        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}`,
        );
        this.goal = response.data || null;
      } catch (error) {
        console.error("Error fetching learning goal:", error);
        this.error = "Failed to load learning goal";
      } finally {
        this.loading = false;
      }
    },

    async initializeAssessment() {
      try {
        this.loading = true;
        this.error = null;

        const [mcqResponse, oralResponse] = await Promise.all([
          apiClient.get(`/web/learning-goals/${this.projectId}/assessment/mcq`),
          apiClient.get(
            `/web/learning-goals/${this.projectId}/assessment/oral`,
          ),
        ]);

        const mcqData = mcqResponse.data;
        const oralData = oralResponse.data;

        this.mcqData = {
          questions: mcqData.questions || [],
          timeLimit: mcqData.time_limit_minutes || 15,
          passingScore: mcqData.passing_score || 70,
          alreadyCompleted: Boolean(mcqData.already_completed),
          previousScore: mcqData.previous_score,
        };

        // Parse Oral data
        this.oralData = {
          questions: oralData.questions || [],
          estimatedTime: oralData.estimated_time_minutes || 15,
          evaluationCriteria: oralData.evaluation_criteria || {},
          alreadyCompleted: Boolean(oralData.already_completed),
          alreadyReviewed: Boolean(oralData.already_reviewed),
          previousScore: oralData.previous_score,
        };

        const mcqCompleted = this.mcqData.alreadyCompleted;
        const oralCompleted = this.oralData.alreadyCompleted;

        if (mcqCompleted && oralCompleted) {
          await this.fetchAssessmentResults();
          return;
        }

        // Show intro first when starting fresh
        if (!mcqCompleted || (mcqCompleted && !oralCompleted)) {
          this.showIntro = true;
          this.showResults = false;
          if (!mcqCompleted) {
            this.currentAssessmentType = "mcq";
          } else {
            this.currentAssessmentType = "oral";
          }
          return;
        }
      } catch (error) {
        console.error("Error initializing assessment:", error);
        this.error =
          error?.response?.data?.detail || "Failed to load assessment";
      } finally {
        this.loading = false;
      }
    },

    proceedFromIntro() {
      this.showIntro = false;

      if (this.currentAssessmentType === "mcq") {
        // Start MCQ assessment
        this.startAssessment("mcq");
        this.startCountdown();
      } else if (this.currentAssessmentType === "oral") {
        // For oral, just show the oral intro (they'll start it manually)
        this.oralAssessmentStarted = false;
      }
    },

    async fetchMCQAssessment() {
      try {
        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}/assessment/mcq`,
        );
        const data = response.data;

        this.mcqData = {
          questions: data.questions || [],
          timeLimit: data.time_limit_minutes || 15,
          passingScore: data.passing_score || 70,
          alreadyCompleted: data.already_completed || false,
          previousScore: data.previous_score,
        };

        if (this.mcqData.alreadyCompleted && !this.oralData.alreadyCompleted) {
          this.currentAssessmentType = "oral";
          this.oralAssessmentStarted = false;
          this.error = null;
        } else if (!this.mcqData.alreadyCompleted) {
          this.startAssessment("mcq");
        }
      } catch (error) {
        console.error("Error fetching MCQ assessment:", error);
        this.error =
          error.response?.data?.detail || "Failed to load MCQ assessment";
      }
    },

    async fetchOralAssessment() {
      try {
        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}/assessment/oral`,
        );
        const data = response.data;

        this.oralData = {
          questions: data.questions || [],
          estimatedTime: data.estimated_time_minutes || 15,
          evaluationCriteria: data.evaluation_criteria || {},
          alreadyCompleted: data.already_completed || false,
          alreadyReviewed: data.already_reviewed || false,
          previousScore: data.previous_score,
        };

        if (this.oralData.alreadyCompleted && this.mcqData.alreadyCompleted) {
          this.error = null;
          await this.fetchAssessmentResults();
        }
      } catch (error) {
        console.error("Error fetching oral assessment:", error);
        this.error =
          error.response?.data?.detail || "Failed to load oral assessment";
      }
    },

    async completeMCQAndStartOral() {
      if (this.submittingMCQ) return;
      try {
        this.submittingMCQ = true;

        const answers = {};
        this.mcqData.questions.forEach((q, idx) => {
          const qid = q && (q.question_id ?? q.id);
          const val = this.selectedAnswers[idx];
          if (qid !== undefined && qid !== null && val !== undefined) {
            answers[qid] = val;
          }
        });

        const res = await apiClient.post(
          `/web/learning-goals/${this.projectId}/assessment/mcq/submit`,
          { answers },
        );
        console.log("MCQ submit result:", res.data);
        this.error = null;

        await this.fetchOralAssessment();

        this.currentQuestionIndex = 0;
        this.currentAssessmentType = "oral";
        this.oralAssessmentStarted = false;
      } catch (err) {
        console.error("Error submitting MCQ:", err);
        this.error =
          err?.response?.data?.detail || "Failed to submit MCQ assessment";
      } finally {
        this.submittingMCQ = false;
      }
    },

    async startOralAssessment() {
      try {
        this.startingOral = true;
        await this.startAssessment("oral");
        this.oralAssessmentStarted = true;
        this.currentOralQuestionIndex = 0;
      } catch (err) {
        console.error("Error starting oral assessment:", err);
        this.error = "Failed to start oral assessment. Please try again.";
      } finally {
        this.startingOral = false;
      }
    },

    async startRecording() {
      try {
        this.recordingSeconds = 0;
        this.recordingTime = "00:00";
        this.recordedBlob = null;
        this.audioChunks = [];

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.mediaStream = stream;

        let mimeType = "";
        if (window.MediaRecorder) {
          if (MediaRecorder.isTypeSupported("audio/webm"))
            mimeType = "audio/webm";
          else if (MediaRecorder.isTypeSupported("audio/ogg"))
            mimeType = "audio/ogg";
          else if (MediaRecorder.isTypeSupported("audio/wav"))
            mimeType = "audio/wav";
        }

        this.mediaRecorder = new MediaRecorder(
          stream,
          mimeType ? { mimeType } : undefined,
        );
        this.recordedMimeType =
          mimeType || this.mediaRecorder.mimeType || "audio/webm";

        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            this.audioChunks.push(e.data);
          }
        };
        this.mediaRecorder.onstop = () => {
          try {
            this.recordedBlob = new Blob(this.audioChunks, {
              type: this.recordedMimeType,
            });
          } catch (e) {
            console.error("Error creating audio blob", e);
            this.recordedBlob = null;
          }
          if (this.recordedBlobUrl) {
            URL.revokeObjectURL(this.recordedBlobUrl);
          }
          if (this.recordedBlob) {
            this.recordedBlobUrl = URL.createObjectURL(this.recordedBlob);
            this.loadAudioMetadata();
          }
          if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((t) => t.stop());
          }
        };

        this.mediaRecorder.start();

        this.recordingInterval = setInterval(() => {
          this.recordingSeconds++;
          const minutes = Math.floor(this.recordingSeconds / 60);
          const seconds = this.recordingSeconds % 60;
          this.recordingTime = `${String(minutes).padStart(2, "0")}:${String(
            seconds,
          ).padStart(2, "0")}`;
        }, 1000);
      } catch (err) {
        console.error("Microphone access/recording failed:", err);
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          this.error =
            "Microphone access was denied. Please allow microphone permissions in your browser settings and try again.";
        } else if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          this.error =
            "No microphone found. Please connect a microphone and try again.";
        } else if (
          err.name === "NotReadableError" ||
          err.name === "TrackStartError"
        ) {
          this.error =
            "Microphone is being used by another application. Please close other apps using the microphone and try again.";
        } else {
          this.error =
            "Failed to access microphone. Please check your device settings and try again.";
        }
      }
    },

    stopRecording() {
      return new Promise((resolve) => {
        if (this.recordingInterval) {
          clearInterval(this.recordingInterval);
          this.recordingInterval = null;
        }
        if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
          const handleStop = () => {
            this.mediaRecorder?.removeEventListener?.("stop", handleStop);
            resolve();
          };
          this.mediaRecorder.addEventListener?.("stop", handleStop);
          this.mediaRecorder.stop();
        } else {
          resolve();
        }
      });
    },

    retryRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
        this.recordingInterval = null;
      }
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop());
        this.mediaStream = null;
      }
      this.audioChunks = [];
      this.recordedBlob = null;
      if (this.recordedBlobUrl) {
        URL.revokeObjectURL(this.recordedBlobUrl);
        this.recordedBlobUrl = null;
      }
      this.recordingSeconds = 0;
      this.recordingTime = "00:00";
      this.currentPlaybackTime = 0;
      this.audioDuration = 0;
      this.isPlaying = false;
      if (this.wavesurfer) {
        this.wavesurfer.destroy();
        this.wavesurfer = null;
      }
    },

    loadAudioMetadata() {
      if (this.recordedBlobUrl) {
        if (this.wavesurfer) {
          this.wavesurfer.destroy();
          this.wavesurfer = null;
        }

        this.$nextTick(() => {
          if (!this.$refs.waveformContainer) return;

          this.wavesurfer = WaveSurfer.create({
            container: this.$refs.waveformContainer,
            waveColor: "#D1D5DB",
            progressColor: "#000000",
            cursorColor: "#000000",
            barWidth: 3,
            barRadius: 3,
            cursorWidth: 2,
            height: 80,
            barGap: 3,
            responsive: true,
            normalize: true,
          });

          this.wavesurfer.load(this.recordedBlobUrl);

          this.wavesurfer.on("ready", () => {
            this.audioDuration = this.wavesurfer.getDuration();
          });

          this.wavesurfer.on("audioprocess", () => {
            this.currentPlaybackTime = this.wavesurfer.getCurrentTime();
          });

          this.wavesurfer.on("play", () => {
            this.isPlaying = true;
          });

          this.wavesurfer.on("pause", () => {
            this.isPlaying = false;
          });

          this.wavesurfer.on("finish", () => {
            this.isPlaying = false;
            this.currentPlaybackTime = 0;
          });
        });
      }
    },

    togglePlayback() {
      if (!this.wavesurfer) return;

      this.wavesurfer.playPause();
    },

    async submitOralAnswer() {
      if (this.submittingOralAnswer) return;

      try {
        this.submittingOralAnswer = true;
        await this.stopRecording();

        if (!this.recordedBlob) {
          this.error = "No audio recorded to submit.";
          return;
        }

        const q = this.currentOralQuestion || {};
        const questionId =
          q.question_id ?? q.id ?? this.currentOralQuestionIndex + 1;

        const form = new FormData();
        form.append("question_id", String(questionId));

        const extMap = {
          "audio/webm": "webm",
          "audio/ogg": "ogg",
          "audio/wav": "wav",
          "audio/x-wav": "wav",
          "audio/mpeg": "mp3",
          "audio/mp3": "mp3",
          "audio/m4a": "m4a",
        };
        const ext = extMap[this.recordedMimeType] || "webm";
        const filename = `oral-q${questionId}.${ext}`;
        form.append("file", this.recordedBlob, filename);

        this.uploadingOral = true;
        await apiClient.post(
          `/web/learning-goals/${this.projectId}/assessment/oral/submit-audio`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (
          this.currentOralQuestionIndex <
          this.oralData.questions.length - 1
        ) {
          this.currentOralQuestionIndex++;
          this.recordingSeconds = 0;
          this.recordingTime = "00:00";
          this.recordedBlob = null;
          this.audioChunks = [];
          if (this.recordedBlobUrl) {
            URL.revokeObjectURL(this.recordedBlobUrl);
            this.recordedBlobUrl = null;
          }
          if (this.wavesurfer) {
            this.wavesurfer.destroy();
            this.wavesurfer = null;
          }
        } else {
          await this.submitAssessment();
        }
      } catch (err) {
        console.error("Error submitting oral audio:", err);
        this.error =
          err?.response?.data?.detail || "Failed to submit oral audio";
      } finally {
        this.uploadingOral = false;
        this.submittingOralAnswer = false;
      }
    },

    startCountdown() {
      // Initialize countdown for MCQ using time limit in minutes
      this.countdownSeconds = (this.mcqData.timeLimit || 15) * 60;
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.countdownInterval = setInterval(() => {
        if (this.countdownSeconds > 0) {
          this.countdownSeconds--;
        } else {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
          // Auto-complete MCQ when time runs out
          if (this.currentAssessmentType === "mcq") {
            this.completeMCQAndStartOral();
          }
        }
      }, 1000);
    },

    completeOralAssessment() {
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
      }

      // In a real application, you would:
      // 1. Stop recording
      // 2. Send the audio file to the backend for analysis

      this.submitAssessment();
    },

    skipOralAssessment() {
      this.submitAssessment();
    },

    async submitAssessment() {
      try {
        this.loading = true;
        await apiClient.post(
          `/web/learning-goals/${this.projectId}/assessment/oral/submit`,
          {},
        );
        if (this.recordingInterval) clearInterval(this.recordingInterval);
        if (this.wavesurfer) {
          this.wavesurfer.pause();
        }

        await this.fetchGoal();

        if (this.shouldShowHumanAssessment) {
          this.showHumanAssessment = true;
          this.loading = false;
        } else {
          await this.fetchAssessmentResults();
        }
      } catch (error) {
        console.error("Error submitting oral assessment:", error);
        this.error =
          error?.response?.data?.detail || "Failed to submit oral assessment";
        this.loading = false;
      }
    },

    scheduleHumanAssessment() {
      this.showBookingModal = true;
    },

    closeBookingModal() {
      this.showBookingModal = false;
    },

    async handleBookMentor() {
      try {
        this.$toast.success("Session booked!");
        this.showBookingModal = false;
        await this.fetchAssessmentBooking();
      } catch (error) {
        console.error("Error handling mentor booking:", error);
      }
    },

    async fetchAssessmentBooking() {
      try {
        const response = await apiClient.get(
          `/web/bookings/project/${this.projectId}/assessment`,
        );
        this.assessmentBooking = response.data;
      } catch (error) {
        console.error("Error fetching assessment booking:", error);
        if (error.response?.status !== 404) {
          this.error =
            error?.response?.data?.detail ||
            "Failed to load assessment booking";
        }
      }
    },

    async viewAssessmentResults() {
      try {
        this.loading = true;
        this.showHumanAssessment = false;
        await this.fetchAssessmentResults();
      } catch (error) {
        console.error("Error loading results:", error);
        this.loading = false;
      }
    },

    goToCertificates() {
      this.$router.push({
        name: "Certificates",
        params: { projectId: this.projectId },
      });
    },

    goBack() {
        this.$router.back();
    },

    requestExit() {
      this.showExitModal = true;
      return new Promise((resolve) => {
        this.pendingExitResolver = resolve;
      });
    },

    cancelExit() {
      this.showExitModal = false;
      if (this.pendingExitResolver) {
        this.pendingExitResolver(false);
        this.pendingExitResolver = null;
      }
    },

    async confirmExit() {
      try {
        if (this.currentAssessmentType === "mcq") {
          await this.completeMCQAndStartOral();
        }
      } catch (e) {
        console.error("Auto-submit on exit failed:", e);
      } finally {
        this.showExitModal = false;
        if (this.pendingExitResolver) {
          this.pendingExitResolver(true);
          this.pendingExitResolver = null;
        }
        this.goBack();
      }
    },

    retryInitialization() {
      this.error = null;
      this.loading = true;
      this.initializeAssessment();
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const options = { year: "numeric", month: "long", day: "numeric" };
      return date.toLocaleDateString("en-US", options);
    },
  },

  created() {
    this.fetchGoal();
    this.initializeAssessment();
    this.fetchAssessmentBooking();
    this._visibilityHandler = async () => {
      if (document.hidden) {
        try {
          if (this.currentAssessmentType === "mcq") {
            await this.completeMCQAndStartOral();
          }
        } catch (e) {
          console.error("Auto-submit on visibilitychange failed:", e);
        }
      }
    };
    document.addEventListener("visibilitychange", this._visibilityHandler);

    this._beforeUnloadHandler = (event) => {
      if (this.showResults) return; // no blocking once results are shown
      event.preventDefault();
      event.returnValue = "";
      this.requestExit();
      return "";
    };
    window.addEventListener("beforeunload", this._beforeUnloadHandler);
  },

  beforeUnmount() {
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.recordedBlobUrl) {
      URL.revokeObjectURL(this.recordedBlobUrl);
      this.recordedBlobUrl = null;
    }
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }
    if (this._visibilityHandler) {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
    }
    if (this._beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this._beforeUnloadHandler);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.showResults) {
      next();
      return;
    }
    if (this.currentAssessmentType) {
      this.requestExit().then((confirmed) => {
        if (confirmed) {
          next();
        } else {
          next(false);
        }
      });
    } else {
      next();
    }
  },
};
</script>
<style scoped></style>
